import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Plus,
  X,
  Filter,
  Search,
  ChevronDown,
  Columns,
  RotateCcw
} from 'lucide-react';
import DateInput from '@/components/ui/date-input';

/**
 * SearchBuilder - MSV Plus style advanced filter component
 * 
 * Props:
 * - fields: Array of { key: string, label: string, type: 'text'|'number'|'date'|'select'|'boolean', options?: [...] }
 * - onFilterChange: (filters: Filter[]) => void
 * - onSearch: (searchTerm: string) => void
 * - columns?: Array of { key: string, label: string, visible: boolean }
 * - onColumnsChange?: (columns) => void
 */

const OPERATORS = {
  text: [
    { value: 'contains', label: 'enthält' },
    { value: 'not_contains', label: 'enthält nicht' },
    { value: 'equals', label: 'ist' },
    { value: 'not_equals', label: 'ist nicht' },
    { value: 'starts_with', label: 'beginnt mit' },
    { value: 'ends_with', label: 'endet mit' },
    { value: 'is_empty', label: 'ist leer' },
    { value: 'is_not_empty', label: 'ist nicht leer' },
  ],
  number: [
    { value: 'equals', label: 'ist gleich' },
    { value: 'not_equals', label: 'ist nicht gleich' },
    { value: 'greater', label: 'größer als' },
    { value: 'greater_or_equal', label: 'größer oder gleich' },
    { value: 'less', label: 'kleiner als' },
    { value: 'less_or_equal', label: 'kleiner oder gleich' },
    { value: 'between', label: 'zwischen' },
  ],
  time: [
    { value: 'greater_or_equal', label: 'ab' },
    { value: 'less_or_equal', label: 'bis' },
    { value: 'equals', label: 'ist' },
    { value: 'greater', label: 'nach' },
    { value: 'less', label: 'vor' },
  ],
  date: [
    { value: 'equals', label: 'ist am' },
    { value: 'before', label: 'vor dem' },
    { value: 'after', label: 'nach dem' },
    { value: 'between', label: 'zwischen' },
    { value: 'is_empty', label: 'ist leer' },
    { value: 'is_not_empty', label: 'ist nicht leer' },
  ],
  select: [
    { value: 'equals', label: 'ist' },
    { value: 'not_equals', label: 'ist nicht' },
    { value: 'in', label: 'ist einer von' },
  ],
  boolean: [
    { value: 'equals', label: 'ist' },
  ],
};

const SearchBuilder = ({
  fields = [],
  onFilterChange,
  onSearch,
  columns,
  onColumnsChange,
  placeholder = "Suche...",
  className = ""
}) => {
  const [filters, setFilters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Notify parent when filters change
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filters);
    }
  }, [filters]);

  // Handle search input
  const handleSearch = (value) => {
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  // Add new filter condition
  const addFilter = () => {
    if (fields.length === 0) return;
    
    const firstField = fields[0];
    const operators = OPERATORS[firstField.type || 'text'];
    
    setFilters([
      ...filters,
      {
        id: Date.now(),
        field: firstField.key,
        fieldType: firstField.type || 'text',
        operator: operators[0].value,
        value: '',
        value2: '', // For 'between' operator
      }
    ]);
    setShowFilters(true);
  };

  // Update filter
  const updateFilter = (id, updates) => {
    setFilters(filters.map(f => {
      if (f.id !== id) return f;
      
      // If field changed, reset operator and value
      if (updates.field && updates.field !== f.field) {
        const newField = fields.find(field => field.key === updates.field);
        const newType = newField?.type || 'text';
        const operators = OPERATORS[newType];
        return {
          ...f,
          ...updates,
          fieldType: newType,
          operator: operators[0].value,
          value: '',
          value2: '',
        };
      }
      
      return { ...f, ...updates };
    }));
  };

  // Remove filter
  const removeFilter = (id) => {
    setFilters(filters.filter(f => f.id !== id));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters([]);
    setSearchTerm('');
    if (onSearch) onSearch('');
  };

  // Get operators for field type
  const getOperators = (fieldType) => {
    return OPERATORS[fieldType] || OPERATORS.text;
  };

  // Get field options for select fields
  const getFieldOptions = (fieldKey) => {
    const field = fields.find(f => f.key === fieldKey);
    return field?.options || [];
  };

  // Toggle column visibility
  const toggleColumn = (key) => {
    if (!onColumnsChange || !columns) return;
    onColumnsChange(columns.map(col => 
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Count active filters
  const activeFilterCount = filters.filter(f => f.value || f.operator === 'is_empty' || f.operator === 'is_not_empty').length;

  return (
    <Card className={`${className}`}>
      <CardContent className="p-4 space-y-4">
        {/* Search Row */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search Input */}
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>

          {/* Add Filter Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={addFilter}
            data-testid="add-filter-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Bedingung
          </Button>

          {/* Toggle Filters */}
          {filters.length > 0 && (
            <Button
              variant={showFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-1" />
              Filter
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-2 bg-white text-brand-red">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          )}

          {/* Column Selector */}
          {columns && onColumnsChange && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Columns className="w-4 h-4 mr-1" />
                  Spalten
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56" align="end">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Sichtbare Spalten</p>
                  {columns.map(col => (
                    <label key={col.key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={col.visible}
                        onChange={() => toggleColumn(col.key)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">{col.label}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Clear Filters */}
          {(filters.length > 0 || searchTerm) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-gray-500"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Zurücksetzen
            </Button>
          )}
        </div>

        {/* Filter Conditions */}
        {showFilters && filters.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            {filters.map((filter, index) => {
              const field = fields.find(f => f.key === filter.field);
              const operators = getOperators(filter.fieldType);
              const options = getFieldOptions(filter.field);
              const needsValue = !['is_empty', 'is_not_empty'].includes(filter.operator);
              const needsSecondValue = filter.operator === 'between';

              return (
                <div 
                  key={filter.id} 
                  className="flex flex-wrap items-center gap-2 p-2 bg-gray-50 rounded-lg"
                  data-testid={`filter-row-${index}`}
                >
                  {/* Field Selector */}
                  <select
                    value={filter.field}
                    onChange={(e) => updateFilter(filter.id, { field: e.target.value })}
                    className="h-9 px-3 rounded-md border text-sm min-w-[140px]"
                  >
                    {fields.map(f => (
                      <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                  </select>

                  {/* Operator Selector */}
                  <select
                    value={filter.operator}
                    onChange={(e) => updateFilter(filter.id, { operator: e.target.value })}
                    className="h-9 px-3 rounded-md border text-sm min-w-[120px]"
                  >
                    {operators.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>

                  {/* Value Input(s) */}
                  {needsValue && (
                    <>
                      {filter.fieldType === 'select' && options.length > 0 ? (
                        <select
                          value={filter.value}
                          onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                          className="h-9 px-3 rounded-md border text-sm min-w-[150px]"
                        >
                          <option value="">Wählen...</option>
                          {options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : filter.fieldType === 'boolean' ? (
                        <select
                          value={filter.value}
                          onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                          className="h-9 px-3 rounded-md border text-sm"
                        >
                          <option value="">Wählen...</option>
                          <option value="true">Ja</option>
                          <option value="false">Nein</option>
                        </select>
                      ) : filter.fieldType === 'date' ? (
                        <>
                          <DateInput
                            
                            value={filter.value}
                            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                            className="h-9 w-[150px]"
                          />
                          {needsSecondValue && (
                            <>
                              <span className="text-sm text-gray-500">und</span>
                              <DateInput
                                
                                value={filter.value2}
                                onChange={(e) => updateFilter(filter.id, { value2: e.target.value })}
                                className="h-9 w-[150px]"
                              />
                            </>
                          )}
                        </>
                      ) : filter.fieldType === 'number' ? (
                        <>
                          <Input
                            type="number"
                            value={filter.value}
                            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                            className="h-9 w-[100px]"
                            placeholder="Wert"
                          />
                          {needsSecondValue && (
                            <>
                              <span className="text-sm text-gray-500">und</span>
                              <Input
                                type="number"
                                value={filter.value2}
                                onChange={(e) => updateFilter(filter.id, { value2: e.target.value })}
                                className="h-9 w-[100px]"
                                placeholder="Wert"
                              />
                            </>
                          )}
                        </>
                      ) : (
                        <Input
                          type="text"
                          value={filter.value}
                          onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                          className="h-9 min-w-[150px] flex-1"
                          placeholder="Wert eingeben..."
                        />
                      )}
                    </>
                  )}

                  {/* Remove Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFilter(filter.id)}
                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Active Filter Summary */}
        {activeFilterCount > 0 && !showFilters && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {filters.filter(f => f.value || f.operator === 'is_empty' || f.operator === 'is_not_empty').map(filter => {
              const field = fields.find(f => f.key === filter.field);
              const operator = getOperators(filter.fieldType).find(op => op.value === filter.operator);
              
              return (
                <Badge 
                  key={filter.id} 
                  variant="secondary"
                  className="flex items-center gap-1 px-2 py-1"
                >
                  <span className="font-medium">{field?.label}</span>
                  <span className="text-gray-500">{operator?.label}</span>
                  {filter.value && <span>"{filter.value}"</span>}
                  <button
                    onClick={() => removeFilter(filter.id)}
                    className="ml-1 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SearchBuilder;

/**
 * Helper function to apply filters to data array
 * @param {Array} data - Array of objects to filter
 * @param {Array} filters - Array of filter objects from SearchBuilder
 * @param {string} searchTerm - Global search term
 * @param {Array} searchFields - Fields to search in (for global search)
 * @returns {Array} - Filtered data
 */
const SKIP_TEXT_KEY = /(^id$|_id$|password|token|secret|created_at|updated_at|created_by)/i;

/** Alle lesbaren Texte eines Datensatzes, ohne Kennungen. */
export function recordText(value, depth = 0) {
  if (value == null || depth > 3) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map((entry) => recordText(entry, depth + 1)).join(' ');
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([key]) => !SKIP_TEXT_KEY.test(key))
      .map(([, entry]) => recordText(entry, depth + 1))
      .join(' ');
  }
  return '';
}

export function freeTextTokens(raw) {
  return String(raw || '')
    .toLowerCase()
    .replace(/[|]+/g, ' ')
    .split(/\s+/)
    .map((token) => token.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ''))
    .map((token) => token.trim())
    .filter(Boolean);
}

/** Jedes Wort muss irgendwo im Datensatz stehen. */
export function matchesFreeText(record, raw) {
  const tokens = freeTextTokens(raw);
  if (!tokens.length) return true;
  const haystack = recordText(record).toLowerCase();
  return tokens.every((token) => haystack.includes(token));
}

function asClock(value) {
  const match = String(value ?? '').trim().match(/(\d{1,2}):(\d{1,2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function compareClock(operator, itemValue, filterValue) {
  const left = asClock(itemValue);
  const right = asClock(filterValue);
  if (left == null || right == null) return false;
  if (operator === 'greater') return left > right;
  if (operator === 'greater_or_equal') return left >= right;
  if (operator === 'less') return left < right;
  if (operator === 'less_or_equal') return left <= right;
  if (operator === 'equals') return left === right;
  return false;
}

export const applyFilters = (data, filters, searchTerm = '', _searchFields = []) => {
  let result = [...data];

  // Freitext über den ganzen Datensatz, nicht nur über eine Feldliste.
  if (searchTerm && freeTextTokens(searchTerm).length) {
    result = result.filter((item) => matchesFreeText(item, searchTerm));
  }

  // Apply each filter
  filters.forEach(filter => {
    if (!filter.value && !['is_empty', 'is_not_empty'].includes(filter.operator)) {
      return; // Skip filters without value (except empty checks)
    }

    result = result.filter(item => {
      const itemValue = item[filter.field];
      const filterValue = filter.value;
      const filterValue2 = filter.value2;

      switch (filter.operator) {
        case 'equals':
          if (filter.fieldType === 'time') return compareClock('equals', itemValue, filterValue);
          if (filter.fieldType === 'boolean') {
            return String(itemValue) === filterValue;
          }
          return String(itemValue).toLowerCase() === String(filterValue).toLowerCase();
        
        case 'not_equals':
          return String(itemValue).toLowerCase() !== String(filterValue).toLowerCase();
        
        case 'contains':
          return String(itemValue || '').toLowerCase().includes(String(filterValue).toLowerCase());
        
        case 'not_contains':
          return !String(itemValue || '').toLowerCase().includes(String(filterValue).toLowerCase());
        
        case 'starts_with':
          return String(itemValue || '').toLowerCase().startsWith(String(filterValue).toLowerCase());
        
        case 'ends_with':
          return String(itemValue || '').toLowerCase().endsWith(String(filterValue).toLowerCase());
        
        case 'greater':
          if (filter.fieldType === 'time') return compareClock('greater', itemValue, filterValue);
          return parseFloat(itemValue) > parseFloat(filterValue);
        
        case 'greater_or_equal':
          if (filter.fieldType === 'time') return compareClock('greater_or_equal', itemValue, filterValue);
          return parseFloat(itemValue) >= parseFloat(filterValue);
        
        case 'less':
          if (filter.fieldType === 'time') return compareClock('less', itemValue, filterValue);
          return parseFloat(itemValue) < parseFloat(filterValue);
        
        case 'less_or_equal':
          if (filter.fieldType === 'time') return compareClock('less_or_equal', itemValue, filterValue);
          return parseFloat(itemValue) <= parseFloat(filterValue);
        
        case 'between':
          const val = filter.fieldType === 'date' ? new Date(itemValue) : parseFloat(itemValue);
          const min = filter.fieldType === 'date' ? new Date(filterValue) : parseFloat(filterValue);
          const max = filter.fieldType === 'date' ? new Date(filterValue2) : parseFloat(filterValue2);
          return val >= min && val <= max;
        
        case 'before':
          return new Date(itemValue) < new Date(filterValue);
        
        case 'after':
          return new Date(itemValue) > new Date(filterValue);
        
        case 'is_empty':
          return itemValue === null || itemValue === undefined || itemValue === '';
        
        case 'is_not_empty':
          return itemValue !== null && itemValue !== undefined && itemValue !== '';
        
        case 'in':
          const values = String(filterValue).split(',').map(v => v.trim().toLowerCase());
          return values.includes(String(itemValue).toLowerCase());
        
        default:
          return true;
      }
    });
  });

  return result;
};
